import { useState } from "react";
import { BadgeEuro, Building2, CarFront, CircleHelp, LayoutDashboard, LogIn, Menu, Newspaper, X } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { NAV_LINKS } from "../../data/home";
import { cn } from "../../lib/format";
import { getSession } from "../../lib/auth";
import { useT } from "../../i18n";
import LanguageSwitcher from "../ui/LanguageSwitcher";
import NotificationBell from "../notifications/NotificationBell";
import MobileTabBar from "./MobileTabBar";

interface HeaderProps {
  /** Pathname of the active route, to highlight the nav link. */
  activeHref?: string;
}

// Icons of the mobile menu, by nav link
const NAV_ICONS: Record<string, LucideIcon> = {
  vehicles: CarFront,
  howItWorks: CircleHelp,
  forCompanies: Building2,
  pricing: BadgeEuro,
  blog: Newspaper,
};

export default function Header({ activeHref }: HeaderProps) {
  const t = useT();
  const [menuOpen, setMenuOpen] = useState(false);
  const session = getSession();
  const account = session
    ? session.role === "company"
      ? { href: "/company", label: t.header.companyArea }
      : { href: "/driver", label: t.header.myAccount }
    : { href: "/login", label: t.common.login };

  return (
    <header className="sticky top-0 z-40 border-b border-line bg-white/97 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-330 items-center gap-4 px-4 sm:px-6">
        <a
          href="/"
          className="whitespace-nowrap text-sm font-semibold tracking-[0.3em] text-ink no-underline"
        >
          LE COIN VTC
        </a>

        {/* Desktop nav */}
        <nav className="mx-auto hidden gap-0.5 lg:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className={cn(
                "rounded px-3.5 py-2 text-sm font-medium text-ink no-underline transition-colors hover:bg-[#F0F0F0]",
                activeHref === link.href && "bg-surface",
              )}
            >
              {t.header.nav[link.key]}
            </a>
          ))}
        </nav>

        <div className="ml-auto hidden items-center gap-2 lg:flex">
          <LanguageSwitcher />
          {session && <NotificationBell />}
          <a
            href={account.href}
            className="rounded px-3.5 py-2 text-sm font-medium text-ink no-underline transition-colors hover:bg-[#F0F0F0]"
          >
            {account.label}
          </a>
          <a
            href="/partners"
            className="flex h-9 items-center rounded bg-brand px-4 text-sm font-medium text-white no-underline transition-colors hover:bg-brand-dark"
          >
            {t.header.listVehicles}
          </a>
        </div>

        {/* Mobile toggle */}
        <div className="ml-auto flex items-center gap-2 lg:hidden">
          <LanguageSwitcher />
          {session && <NotificationBell />}
          <button
            type="button"
            onClick={() => setMenuOpen((o) => !o)}
            aria-expanded={menuOpen}
            aria-label={menuOpen ? t.common.close : t.header.menu}
            title={menuOpen ? t.common.close : t.header.menu}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded border-0 bg-surface text-ink"
          >
            {menuOpen ? <X size={20} aria-hidden="true" /> : <Menu size={20} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="flex flex-col border-t border-line bg-white px-4 pb-4 pt-2 lg:hidden">
          {NAV_LINKS.map((link) => {
            const Icon = NAV_ICONS[link.key] ?? CircleHelp;
            return (
              <a
                key={link.href}
                href={link.href}
                aria-current={activeHref === link.href ? "page" : undefined}
                className={cn(
                  "flex items-center gap-3 rounded px-3 py-2.5 text-[15px] font-medium no-underline hover:bg-surface",
                  activeHref === link.href ? "bg-surface text-brand" : "text-ink",
                )}
              >
                <Icon size={19} strokeWidth={1.8} className="shrink-0 text-muted" aria-hidden="true" />
                {t.header.nav[link.key]}
              </a>
            );
          })}
          <a href={account.href} className="flex items-center gap-3 rounded px-3 py-2.5 text-[15px] font-medium text-ink no-underline hover:bg-surface">
            {session ? (
              <LayoutDashboard size={19} strokeWidth={1.8} className="shrink-0 text-muted" aria-hidden="true" />
            ) : (
              <LogIn size={19} strokeWidth={1.8} className="shrink-0 text-muted" aria-hidden="true" />
            )}
            {account.label}
          </a>
          <a
            href="/partners"
            className="mt-2.5 flex h-11 items-center justify-center rounded bg-brand text-sm font-medium text-white no-underline"
          >
            {t.header.listVehicles}
          </a>
        </div>
      )}
      <MobileTabBar account={account} />
    </header>
  );
}
